// Exact decimal-string arithmetic at scale 4, backed by BigInt -- never a
// float, never integer cents. Used for day totals (excluding removed/linked
// rows) and the split editor's residual, per the execution plan's binding
// rule: "Splits sum to the charge exactly... compare as decimal strings at
// scale 4 -- never floats, never integer cents."
const SCALE = 4;
const SCALE_FACTOR = 10n ** BigInt(SCALE);

function toScaled(value: string): bigint {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-') || trimmed.startsWith('−');
  const unsigned = trimmed.replace(/^[-−]/, '');
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.');
  const intPart = intPartRaw === '' ? '0' : intPartRaw;
  const fracPart = (fracPartRaw + '0'.repeat(SCALE)).slice(0, SCALE);
  const magnitude = BigInt(intPart) * SCALE_FACTOR + BigInt(fracPart || '0');
  return negative ? -magnitude : magnitude;
}

function fromScaled(scaled: bigint): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const digits = magnitude.toString().padStart(SCALE + 1, '0');
  const intPart = digits.slice(0, digits.length - SCALE);
  const fracPart = digits.slice(digits.length - SCALE);
  return `${negative ? '-' : ''}${intPart}.${fracPart}`;
}

export function decimalAdd(a: string, b: string): string {
  return fromScaled(toScaled(a) + toScaled(b));
}

export function decimalSubtract(a: string, b: string): string {
  return fromScaled(toScaled(a) - toScaled(b));
}

export function decimalSum(values: string[]): string {
  return fromScaled(values.reduce((acc, v) => acc + toScaled(v), 0n));
}

export function decimalIsZero(value: string): boolean {
  return toScaled(value) === 0n;
}

export function decimalIsNegative(value: string): boolean {
  return toScaled(value) < 0n;
}

export function decimalAbs(value: string): string {
  const scaled = toScaled(value);
  return fromScaled(scaled < 0n ? -scaled : scaled);
}
